/**
 * @file AiConsentHost.tsx
 * @brief The "How VERBLIZR uses AI" sheet (Apple 5.1.2(i), BACKEND B7), web
 *        and the Mac app. Mounted once in App; opened by consentBridge.
 *
 * @details
 * Renders the server's wording as is (title, body, Agree / Not now, privacy
 * link). "Agree" posts the version shown; a 409 AI_CONSENT_VERSION_STALE
 * swaps in the newer text and asks again. "Not now", Esc or ✕ answer false:
 * the caller starts nothing. With no wording yet (a 403 without `consent`),
 * the text is read from GET /api/auth/ai-consent first.
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import { ExternalLink, LoaderCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/Dialog";
import { ApiClientError, apiPost } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { queryClient } from "@/shared/api/queryClient";
import {
  AI_CONSENT_VERSION_STALE,
  parseAiConsentState,
  wordingFromRefusal,
  type AiConsentWording,
} from "./aiConsent";
import {
  aiConsentKey,
  fetchAiConsentState,
  getAiConsentRequest,
  replaceAiConsentWording,
  settleAiConsent,
  subscribeAiConsent,
} from "./consentBridge";

function errorBody(err: unknown): unknown {
  if (!(err instanceof ApiClientError)) return null;
  return (err.raw as { response?: { data?: unknown } } | undefined)?.response?.data ?? null;
}

export function AiConsentHost() {
  const request = useSyncExternalStore(subscribeAiConsent, getAiConsentRequest, getAiConsentRequest);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wording: AiConsentWording | null = request?.wording ?? null;

  // A 403 without the text: read it.
  useEffect(() => {
    if (!request || request.wording) return;
    let gone = false;
    fetchAiConsentState()
      .then((s) => {
        if (!gone && s) replaceAiConsentWording(s.wording);
        else if (!gone) setError("Couldn't load the text. Check your connection and try again.");
      })
      .catch(() => {
        if (!gone) setError("Couldn't load the text. Check your connection and try again.");
      });
    return () => {
      gone = true;
    };
  }, [request]);

  useEffect(() => {
    if (!request) {
      setBusy(false);
      setError(null);
    }
  }, [request]);

  const agree = async () => {
    if (!wording) return;
    setBusy(true);
    setError(null);
    try {
      const saved = parseAiConsentState(await apiPost<unknown>(authEndpoints.aiConsent, { version: wording.version }));
      if (saved) queryClient.setQueryData(aiConsentKey, saved);
      else void queryClient.invalidateQueries({ queryKey: aiConsentKey });
      settleAiConsent(true);
    } catch (err) {
      const newer = err instanceof ApiClientError && err.code === AI_CONSENT_VERSION_STALE
        ? wordingFromRefusal(errorBody(err))
        : null;
      if (newer) {
        replaceAiConsentWording(newer);
        setError(err instanceof ApiClientError ? err.message : null);
      } else {
        setError(err instanceof ApiClientError ? err.message : "Couldn't save your answer. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!request}
      onOpenChange={(open) => {
        if (!open && !busy) settleAiConsent(false);
      }}
    >
      <DialogContent className="max-w-md gap-5" aria-describedby="ai-consent-body">
        <Sparkles className="h-10 w-10 text-brand" strokeWidth={1.25} />
        <DialogTitle className="text-xl font-semibold tracking-tight [text-wrap:balance]">
          {wording?.title ?? "How VERBLIZR uses AI"}
        </DialogTitle>
        <DialogDescription id="ai-consent-body" className="text-sm leading-relaxed text-muted-foreground">
          {wording?.body ?? "Loading…"}
        </DialogDescription>
        {wording ? (
          <a
            href={wording.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
          >
            Read the Privacy Policy <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <Button
            type="button"
            onClick={() => void agree()}
            disabled={busy || !wording}
            className="h-11 flex-1 gap-2 bg-brand font-semibold text-brand-foreground hover:bg-brand/90"
          >
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            {wording?.agreeLabel ?? "Agree"}
          </Button>
          <Button type="button" variant="ghost" className="h-11 flex-1" disabled={busy} onClick={() => settleAiConsent(false)}>
            {wording?.declineLabel ?? "Not now"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
